import { useEffect } from "react";
import { Link } from "react-router-dom";
import { games } from "@/mocks/games";

const guideHero =
  "https://readdy.ai/api/search-image?query=Stylized%20editorial%20illustration%20of%20a%20warm%20reading%20desk%20at%20night%20with%20glowing%20book%20and%20game%20icons%2C%20a%20lantern%20and%20open%20notebook%2C%20cozy%20guided%20mood%2C%20warm%20amber%20and%20cream%20palette%2C%20magazine%20art%2C%20no%20text&width=1600&height=700&seq=guide-hero-001&orientation=landscape";

const steps = [
  { icon: "ri-questionnaire-line", title: "明确你的偏好", desc: "先想清楚自己更看重剧情深度、轻松日常，还是烧脑悬疑。偏好决定了你该从哪个分类入手。" },
  { icon: "ri-menu-search-line", title: "看准口碑与评分", desc: "参考幻夜志的评分与「神作/口碑作」标签，优先选择评分高、社区讨论稳定的作品，降低踩雷概率。" },
  { icon: "ri-shopping-cart-line", title: "选对购买渠道", desc: "确认作品在 Steam、DLSite 或官方商城是否有你所在地区可用的版本，并留意是否有官方中文与 R18 补丁。" },
  { icon: "ri-time-line", title: "规划游玩节奏", desc: "先玩一部短篇熟悉节奏，再逐步挑战长篇史诗。评测正文都标注了预估时长，方便你安排。" },
];

const pitfalls = [
  { title: "只看标签不看内容", desc: "同类型作品差异巨大，务必先读评测正文，确认题材与尺度是否符合你的接受范围。" },
  { title: "忽略补丁与版本", desc: "部分作品需要额外安装 R18 补丁才能解锁成人内容，购买前务必确认是否包含或可单独获取。" },
  { title: "贪图全集贪多嚼不烂", desc: "一次买太多反而消化不良。建议一次专注 1—2 部，认真体验完再开启下一段旅程。" },
  { title: "忽略年龄与地区合规", desc: "请确认自己已年满 18 周岁，并遵守所在地区的法律法规与平台年龄验证要求。" },
];

export default function Guide() {
  const beginner = games.filter((g) => g.isBeginner);

  useEffect(() => {
    document.title = "工口游戏入门指南 | 幻夜志";
  }, []);

  return (
    <>
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img src={guideHero} alt="入门指南背景" className="h-full w-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-background-50"></div>
        </div>
        <div className="relative mx-auto w-full max-w-7xl px-4 pb-16 pt-16 md:px-6 md:pb-20 md:pt-20">
          <nav aria-label="面包屑" className="text-xs text-background-100/80">
            <ol className="flex items-center gap-1.5">
              <li><Link to="/" className="hover:text-background-50">首页</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li aria-current="page" className="font-medium text-background-50">入门指南</li>
            </ol>
          </nav>
          <h1 className="mt-5 max-w-2xl font-display text-3xl font-black leading-snug text-background-50 md:text-4xl">
            新手入门指南
            <span className="mt-1 block text-xl font-bold text-primary-300 md:text-2xl">第一次玩工口游戏，看这篇就够了</span>
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-100/90 md:text-base">
            从零开始的完整入坑路线：先搞清楚自己的偏好，再避开常见雷区，
            最后跟着推荐清单迈出第一步。四步走，让你不再大海捞针。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-16">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-accent-600">Step by Step</p>
        <h2 className="mt-2 font-display text-2xl font-bold text-foreground-950">入坑四步走</h2>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => (
            <div key={s.title} className="relative rounded-2xl border border-background-200/70 bg-background-100 p-6">
              <span className="font-display absolute right-5 top-5 text-4xl font-black text-background-200">{String(i + 1).padStart(2, "0")}</span>
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className={`${s.icon} text-xl`}></i>
              </div>
              <h3 className="mt-4 font-display text-base font-bold text-foreground-950">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background-100/70">
        <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-16">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-accent-600">First Picks</p>
          <h2 className="mt-2 font-display text-2xl font-bold text-foreground-950">入门推荐清单</h2>
          <p className="mt-3 max-w-2xl text-sm text-foreground-600">
            以下四部都是系统简单、剧情友好、单周目体量适中的代表作，非常适合作为你的第一部。
          </p>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {beginner.map((g) => (
              <Link key={g.id} to={`/review/${g.id}`} className="group flex gap-5 rounded-2xl border border-background-200/70 bg-background-50 p-5 transition-all hover:-translate-y-1 hover:border-primary-300">
                <img src={g.cover} alt={`${g.title} 封面`} className="h-40 w-28 shrink-0 rounded-xl object-cover object-top" />
                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate font-display text-lg font-bold text-foreground-950 group-hover:text-accent-700">{g.title}</h3>
                    <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">{g.score.toFixed(1)} 分</span>
                  </div>
                  <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">{g.blurb}</p>
                  <div className="mt-auto flex flex-wrap gap-1.5 pt-3">
                    {g.tags.map((t) => (
                      <span key={t} className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs text-secondary-700">{t}</span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-16">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-accent-600">Avoid Pitfalls</p>
        <h2 className="mt-2 font-display text-2xl font-bold text-foreground-950">避坑指南</h2>
        <div className="mt-8 grid gap-6 sm:grid-cols-2">
          {pitfalls.map((p) => (
            <div key={p.title} className="flex gap-4 rounded-2xl border border-background-200/70 bg-background-100 p-6">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                <i className="ri-alert-line text-xl"></i>
              </span>
              <div>
                <h3 className="font-display text-base font-bold text-foreground-950">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-start justify-between gap-4 rounded-2xl bg-background-950 p-8 sm:flex-row sm:items-center md:p-10">
          <div>
            <h3 className="font-display text-xl font-bold text-background-50">准备好了？去看看完整榜单</h3>
            <p className="mt-2 text-sm text-background-100/80">按口碑和类型筛选，锁定你的下一部作品。</p>
          </div>
          <Link to="/ranking" className="inline-flex shrink-0 items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600">
            打开排行榜 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>
    </>
  );
}